/**
 * Subscription pricing: the ONE place a subscription price or a charge is worked out.
 *
 * Every price, the tax rate, the card fee, the proration and the plan-to-plan credit live here.
 * Nothing else in the app may declare a price or repeat this arithmetic -- not a component, not a
 * page, not a service. Import from here instead.
 *
 * Why this file exists: the same proration was copy-pasted into five modals, each with its own
 * constants or its own small differences, and the Pro upgrade ended up telling a team that Pro
 * costs $500 per attorney per year. It costs $1,000. The $500 is the part left to pay once the
 * Basic plan they already bought is credited, which is a different sentence and a different
 * number to store.
 *
 * `pricing.test.ts` locks the published prices and the worked examples. If a price changes, it
 * changes here and the test changes with it, deliberately.
 */

/** What is being billed. `pro_upgrade` is Pro sold to a team already paying for Basic. */
export type BillingPreset = 'basic' | 'basic_monthly' | 'pro' | 'pro_upgrade' | 'pro_monthly';

// ---------------------------------------------------------------------------
// The published prices, per attorney, per period. The only copies in the app.
// ---------------------------------------------------------------------------

export const BASIC_BASE = 500;
export const PRO_BASE = 1000;

// Monthly is a tenth of the year, so a year bought a month at a time costs more than a year
// bought outright and the annual commitment stays the cheaper one.
export const PRO_MONTHLY_BASE = PRO_BASE / 10;
export const BASIC_MONTHLY_BASE = BASIC_BASE / 10;

export const TAX_RATE = 0.07;
export const CARD_FEE_RATE = 0.029;
export const CARD_FEE_FIXED = 0.30;

const DAYS_IN_YEAR = 365;
const DAYS_IN_MONTH = 30;

export interface PlanDefinition {
    preset: BillingPreset;
    /** How the plan is named on a receipt or a summary line. */
    label: string;
    /** The plan's own price per attorney per period. What the customer is buying. */
    listPerAttorney: number;
    /**
     * Per attorney per period that the plan being replaced has already paid for, and which is
     * therefore taken off the list price. Zero for every plan bought outright.
     */
    creditPerAttorney: number;
    /** Names the credit on a summary line. Null when there is no credit. */
    creditLabel: string | null;
    /**
     * Per attorney per period actually charged: list minus credit. This is the number the
     * proration and the totals run on -- never the list price, and never a hard-coded delta.
     */
    chargedPerAttorney: number;
    periodLabel: 'year' | 'month';
    daysInPeriod: number;
    paymentsPerYear: number;
}

function definePlan(
    preset: BillingPreset,
    label: string,
    listPerAttorney: number,
    period: 'year' | 'month',
    credit: { amount: number; label: string } | null,
): PlanDefinition {
    return {
        preset,
        label,
        listPerAttorney,
        creditPerAttorney: credit?.amount ?? 0,
        creditLabel: credit?.label ?? null,
        chargedPerAttorney: listPerAttorney - (credit?.amount ?? 0),
        periodLabel: period,
        daysInPeriod: period === 'year' ? DAYS_IN_YEAR : DAYS_IN_MONTH,
        paymentsPerYear: period === 'year' ? 1 : 12,
    };
}

export const PLANS: Record<BillingPreset, PlanDefinition> = {
    basic: definePlan('basic', 'Basic', BASIC_BASE, 'year', null),
    basic_monthly: definePlan('basic_monthly', 'Basic Monthly', BASIC_MONTHLY_BASE, 'month', null),
    pro: definePlan('pro', 'Pro', PRO_BASE, 'year', null),
    // Pro, sold to a team already paying for Basic: they are buying the $1,000 plan and the $500
    // they already paid comes off it. Both halves are kept, so a summary can say so rather than
    // quoting $500 as the price of Pro.
    pro_upgrade: definePlan('pro_upgrade', 'Pro', PRO_BASE, 'year', {
        amount: BASIC_BASE,
        label: 'Less Basic plan already paid',
    }),
    pro_monthly: definePlan('pro_monthly', 'Pro Monthly', PRO_MONTHLY_BASE, 'month', null),
};

export function planFor(preset: BillingPreset): PlanDefinition {
    return PLANS[preset];
}

/** The Standard-to-Pro difference. Derived, never typed in. */
export const PRO_UPGRADE_BASE = PLANS.pro_upgrade.chargedPerAttorney;

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** Every money figure this module returns has been through this. */
export function roundCurrency(amount: number): number {
    return Math.round(amount * 100) / 100;
}

/** Stripe's card fee, charged on the taxed subtotal. */
export function calcCardFee(subtotalWithTax: number): number {
    return roundCurrency(subtotalWithTax * CARD_FEE_RATE + CARD_FEE_FIXED);
}

/**
 * How the tax and the card fee are described to a customer.
 *
 * Here rather than typed into each screen: "Taxes (7%)" was written out in five components and
 * "(base + taxes) x 2.9% + $0.30" in ten, so changing a rate meant changing the arithmetic in one
 * place and the sentences in fifteen others, and any that were missed would state a rate the
 * charge did not use.
 */
export const TAX_RATE_LABEL = `${roundCurrency(TAX_RATE * 100)}%`;

export const CARD_FEE_RATE_LABEL = `${roundCurrency(CARD_FEE_RATE * 100)}%`;

export const CARD_FEE_FIXED_LABEL = `$${CARD_FEE_FIXED.toFixed(2)}`;

/** The card fee written as the formula it is: "(base + taxes) x 2.9% + $0.30". */
export const CARD_FEE_FORMULA_LABEL = `(base + taxes) \u00d7 ${CARD_FEE_RATE_LABEL} + ${CARD_FEE_FIXED_LABEL}`;

export interface TaxedTotal {
    base: number;
    tax: number;
    cardFee: number;
    total: number;
}

/** Tax, then the card fee on top of the taxed amount. The order matters and is fixed here. */
export function applyTaxAndFees(base: number, useCard: boolean): TaxedTotal {
    const rounded = roundCurrency(base);
    const tax = roundCurrency(rounded * TAX_RATE);
    const cardFee = useCard ? calcCardFee(rounded + tax) : 0;
    return { base: rounded, tax, cardFee, total: roundCurrency(rounded + tax + cardFee) };
}

/** Kept for callers that price a flat per-attorney amount with no plan behind it. */
export function calcTotal(basePerAttorney: number, attorneyCount: number, useCard: boolean): TaxedTotal {
    return applyTaxAndFees(basePerAttorney * attorneyCount, useCard);
}

// ---------------------------------------------------------------------------
// Charges
// ---------------------------------------------------------------------------

export interface ChargeBreakdown {
    preset: BillingPreset;
    /** Plan name for a summary line. */
    label: string;
    periodLabel: 'year' | 'month';
    attorneyCount: number;

    /** The plan's list price per attorney, and for the whole team. What Pro actually costs. */
    listPerAttorney: number;
    listBase: number;
    /** What the plan being replaced already covers. Zero for a plan bought outright. */
    creditPerAttorney: number;
    creditBase: number;
    creditLabel: string | null;

    /** List minus credit, per attorney and for the team. Proration runs on this. */
    chargedPerAttorney: number;
    /** The team's full-period base before any proration. */
    fullBasePerPeriod: number;

    /** True when the charge covers part of a period rather than a whole one. */
    proRated: boolean;
    dailyBaseRate: number;
    /** Days being charged for. Equals the whole period when not prorated. */
    days: number;
    /** The base actually being charged, after proration. */
    proRatedBase: number;
    /**
     * When the next billing date is more than one period out, the days beyond the period are
     * still charged; this is that amount. Zero in the ordinary case.
     */
    additionalOverage: number;

    tax: number;
    cardFee: number;
    total: number;
}

export interface ChargeInput {
    preset: BillingPreset;
    attorneyCount: number;
    useCard: boolean;
    /** Charges the days up to this date instead of a whole period. */
    nextBillingDate?: string | null;
    /** Injectable for tests. Defaults to now. */
    now?: Date;
}

/**
 * The date proration counts towards: the team's next billing date, rolled forward by one period
 * if it has already passed.
 */
export function nextBillingForProRate(
    preset: BillingPreset,
    nextBillingDate: string | null | undefined,
    now: Date = new Date(),
): Date | null {
    if (!nextBillingDate) return null;
    const date = new Date(nextBillingDate);
    if (isNaN(date.getTime())) return null;
    if (date > now) return date;

    // Rolls until it lands ahead of now, not once: a monthly team whose billing date is several
    // months stale would otherwise still be given a date in the past, and be charged for nothing.
    // In UTC: billing dates are UTC midnights, and rolling in local time moved them by an hour
    // across a daylight-saving change, which could add a day to the count.
    const monthly = planFor(preset).periodLabel === 'month';
    const rolled = new Date(date);
    do {
        if (monthly) {
            rolled.setUTCMonth(rolled.getUTCMonth() + 1);
        } else {
            rolled.setUTCFullYear(rolled.getUTCFullYear() + 1);
        }
    } while (rolled <= now);
    return rolled;
}

function breakdown(
    plan: PlanDefinition,
    attorneyCount: number,
    useCard: boolean,
    days: number,
    proRated: boolean,
): ChargeBreakdown {
    const fullBasePerPeriod = roundCurrency(plan.chargedPerAttorney * attorneyCount);
    const dailyBaseRate = fullBasePerPeriod / plan.daysInPeriod;
    const proRatedBase = proRated ? roundCurrency(dailyBaseRate * days) : fullBasePerPeriod;
    const additionalOverage = proRatedBase > fullBasePerPeriod
        ? roundCurrency(proRatedBase - fullBasePerPeriod)
        : 0;
    const totals = applyTaxAndFees(proRatedBase, useCard);

    return {
        preset: plan.preset,
        label: plan.label,
        periodLabel: plan.periodLabel,
        attorneyCount,

        listPerAttorney: plan.listPerAttorney,
        listBase: roundCurrency(plan.listPerAttorney * attorneyCount),
        creditPerAttorney: plan.creditPerAttorney,
        creditBase: roundCurrency(plan.creditPerAttorney * attorneyCount),
        creditLabel: plan.creditLabel,

        chargedPerAttorney: plan.chargedPerAttorney,
        fullBasePerPeriod,

        proRated,
        dailyBaseRate,
        days,
        proRatedBase: totals.base,
        additionalOverage,

        tax: totals.tax,
        cardFee: totals.cardFee,
        total: totals.total,
    };
}

/** A whole period, no proration. */
export function fullCharge({ preset, attorneyCount, useCard }: ChargeInput): ChargeBreakdown {
    const plan = planFor(preset);
    return breakdown(plan, attorneyCount, useCard, plan.daysInPeriod, false);
}

/**
 * The days between now and the team's next billing date. Null when there is no usable date, or
 * when that date is today -- there is nothing to charge for, and the caller falls back to
 * `fullCharge`.
 */
export function proRatedCharge({
    preset,
    attorneyCount,
    useCard,
    nextBillingDate,
    now = new Date(),
}: ChargeInput): ChargeBreakdown | null {
    const target = nextBillingForProRate(preset, nextBillingDate, now);
    if (!target) return null;

    const days = Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    if (days <= 0) return null;

    return breakdown(planFor(preset), attorneyCount, useCard, days, true);
}

export interface PlanChangeInput {
    /** The plan the team is on now. Its period is the one being split. */
    fromPreset: BillingPreset;
    /** The plan the team moves to. */
    toPreset: BillingPreset;
    attorneyCount: number;
    /** The team's next billing date (the override date when one is set). */
    nextBillingDate: string | null | undefined;
    /** Injectable for tests. Defaults to now. */
    now?: Date;
}

export interface PlanChangeProration {
    fromPreset: BillingPreset;
    toPreset: BillingPreset;
    /** The paid period the change falls in: one period of the current plan, ending at periodEnd. */
    periodStart: string;
    periodEnd: string;
    /** Days of the paid period still to run. */
    days: number;
    /** Untaxed: the current plan for those days, given back. */
    oldPlanCredit: number;
    /** Untaxed: the new plan for the same days. */
    newPlanCharge: number;
    /** newPlanCharge - oldPlanCredit. Positive is owed now; negative is a credit. Untaxed. */
    net: number;
}

/**
 * The money for changing a team's plan or billing frequency part-way through a paid period.
 *
 * The period ends at the team's next billing date, rolled forward the way every other proration
 * rolls it when the date has already passed, and starts one period of the CURRENT plan before
 * that. Only the days left in that period move: the current plan for those days is credited and
 * the new plan for the same days is charged, each at its own daily rate. Time before the period
 * start (a billing date set further out than one period, e.g. a comped period) was never paid
 * for, so it is not credited.
 *
 * Null when there is no usable billing date or no days are left.
 */
export function planChangeProration({
    fromPreset,
    toPreset,
    attorneyCount,
    nextBillingDate,
    now = new Date(),
}: PlanChangeInput): PlanChangeProration | null {
    if (attorneyCount <= 0) return null;

    const periodEnd = nextBillingForProRate(fromPreset, nextBillingDate, now);
    if (!periodEnd) return null;

    const fromPlan = planFor(fromPreset);
    const periodStart = new Date(periodEnd);
    if (fromPlan.periodLabel === 'month') {
        periodStart.setUTCMonth(periodStart.getUTCMonth() - 1);
    } else {
        periodStart.setUTCFullYear(periodStart.getUTCFullYear() - 1);
    }

    const from = periodStart > now ? periodStart : now;
    const days = Math.min(
        fromPlan.daysInPeriod,
        Math.max(0, Math.ceil((periodEnd.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))),
    );
    if (days <= 0) return null;

    const oldPlanCredit = breakdown(fromPlan, attorneyCount, false, days, true).proRatedBase;
    const newPlanCharge = breakdown(planFor(toPreset), attorneyCount, false, days, true).proRatedBase;

    return {
        fromPreset,
        toPreset,
        periodStart: periodStart.toISOString(),
        periodEnd: periodEnd.toISOString(),
        days,
        oldPlanCredit,
        newPlanCharge,
        net: roundCurrency(newPlanCharge - oldPlanCredit),
    };
}

/** What a caller almost always wants: prorated when a billing date allows it, otherwise full. */
export function chargeFor(input: ChargeInput): ChargeBreakdown {
    return proRatedCharge(input) ?? fullCharge(input);
}

/** What the team pays at its next full renewal. No proration, card fee optional. */
export function nextPaymentBreakdown(
    preset: BillingPreset,
    attorneyCount: number,
    useCard: boolean = false,
): ChargeBreakdown {
    return fullCharge({ preset, attorneyCount, useCard });
}

/**
 * What a team saves per year by paying from a bank account instead of a card: the card fee on
 * every payment they would make in a year.
 */
export function cardFeeSavings(preset: BillingPreset, attorneyCount: number): {
    plan: PlanDefinition;
    base: number;
    tax: number;
    basePlusTax: number;
    cardFeePerPayment: number;
    paymentsPerYear: number;
    savingsPerPeriod: number;
    savingsPerYear: number;
} {
    const plan = planFor(preset);
    const charged = fullCharge({ preset, attorneyCount, useCard: true });
    const savingsPerYear = roundCurrency(charged.cardFee * plan.paymentsPerYear);

    return {
        plan,
        base: charged.proRatedBase,
        tax: charged.tax,
        basePlusTax: roundCurrency(charged.proRatedBase + charged.tax),
        cardFeePerPayment: charged.cardFee,
        paymentsPerYear: plan.paymentsPerYear,
        savingsPerPeriod: roundCurrency(savingsPerYear / plan.paymentsPerYear),
        savingsPerYear,
    };
}

/**
 * The plan a team on this subscription type and billing frequency is on.
 *
 * A renewal is not a different product from a new subscription, so it is not priced differently:
 * both go through this and then through the same charge functions. There is no renewal price
 * table, and there must never be one again -- Standard renewals used to run a separate ladder
 * ($100 for the first attorney plus $50 for each one after) that had nothing to do with the $500
 * a new Standard subscription costs.
 */
export function presetForSubscription(
    subscriptionTypeCode: string | null | undefined,
    billingFrequencyCode: string | null | undefined,
): BillingPreset | null {
    const type = (subscriptionTypeCode ?? '').trim().toLowerCase();
    const monthly = (billingFrequencyCode ?? '').trim().toLowerCase() === 'monthly';

    if (type === 'pro') return monthly ? 'pro_monthly' : 'pro';
    if (type === 'standard' || type === 'basic') return monthly ? 'basic_monthly' : 'basic';
    return null;
}

/**
 * What one renewal period costs: the plan's full price for the team, with tax.
 *
 * No card fee: the amount is worked out when the charge is queued, and which payment method will
 * settle it is not known until the job charges it.
 */
export function renewalCharge(
    subscriptionTypeCode: string | null | undefined,
    billingFrequencyCode: string | null | undefined,
    attorneyCount: number,
): ChargeBreakdown | null {
    const preset = presetForSubscription(subscriptionTypeCode, billingFrequencyCode);
    if (!preset || attorneyCount <= 0) return null;
    return fullCharge({ preset, attorneyCount, useCard: false });
}
